import React, { useEffect } from 'react';
import { LoadingSpinner, Tabs } from 'components/common';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getUser, unlockUser } from 'redux/authorization/userSlice';
import {
  FaInfoCircle, FaUsers, FaUserShield, FaHistory, FaKey,
  FaLock, FaUnlock, FaCheckCircle, FaTimesCircle, FaEnvelope,
} from 'react-icons/fa';
import { MdWarning } from 'react-icons/md';
import UserDetailHeader    from 'components/app/authorization/user/detail/UserDetailHeader';
import UserDetailInfo      from 'components/app/authorization/user/detail/UserDetailInfo';
import UserDetailEmployeeMaping from 'components/app/authorization/user/detail/UserDetailEmployeeMaping';
import UserDetailAuthorization  from 'components/app/authorization/user/detail/UserDetaillAuthorization';
import UserActivity        from 'components/app/authorization/user/detail/UserActivity';
import UserPlatformStatus  from 'components/app/authorization/user/detail/UserPlatformStatus';
import useBreadcrumb from 'hooks/useBreadcrumb';

const UserDetailPage = () => {
  const { id: userId } = useParams();
  const dispatch  = useDispatch();
  const { recordData: user } = useSelector(state => state.authorization_user);

  // Breadcrumb: hiển thị tên user thay vì id
  useBreadcrumb(
    user
      ? [
          { label: 'Users', to: '/authorization/users' },
          { label: user.full_name || user.name || `User ${userId}` }
        ]
      : null
  );

  useEffect(() => {
    dispatch(getUser(userId));
  }, [dispatch, userId]);

  const handleUnlock = () => dispatch(unlockUser(userId));

  if (!user || Object.keys(user).length === 0) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-min-h-64">
        <div className="tw-text-center">
          <LoadingSpinner size="lg" />
          <p className="tw-text-sm tw-text-gray-500 tw-mt-3">Loading user details…</p>
        </div>
      </div>
    );
  }

  const active = user.active || false;
  const locked = user.access_locked && active;

  return (
    <div className="tw-min-h-screen tw-bg-gray-50">
      <UserDetailHeader user={user} />

      {/* ── Profile banner ── */}
      <div className="tw-bg-white tw-border-b tw-border-gray-200 tw-shadow-sm tw-mb-4">
        <div className="tw-px-6 tw-py-5">
          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-justify-between tw-gap-4">
            {/* Avatar + name */}
            <div className="tw-flex tw-items-center tw-gap-4">
              <div className="tw-relative">
                <div className="tw-w-14 tw-h-14 tw-rounded-xl tw-bg-gradient-to-br tw-from-blue-500 tw-to-violet-600 tw-flex tw-items-center tw-justify-center tw-text-white tw-text-xl tw-font-bold tw-shadow-md tw-select-none">
                  {(user.full_name || user.name || '?')[0].toUpperCase()}
                </div>
                {active && (
                  <span className="tw-absolute tw-bottom-0 tw-right-0 tw-w-3.5 tw-h-3.5 tw-rounded-xl tw-bg-green-500 tw-border-2 tw-border-white" />
                )}
              </div>
              <div>
                <h1 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-leading-tight">
                  {user.full_name || user.name}
                </h1>
                <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-2 tw-mt-1">
                  <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-sm tw-bg-gray-100 tw-text-gray-600 tw-text-xs tw-font-mono tw-font-semibold">
                    {user.user_code}
                  </span>
                  {user.email && (
                    <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-500">
                      <FaEnvelope size={10} />
                      {user.email}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Status badges */}
            <div className="tw-flex tw-flex-col tw-items-end tw-gap-1.5">
              <span className="tw-text-xs tw-font-semibold tw-tracking-wider tw-text-gray-400 tw-uppercase">Account Status</span>
              <div className="tw-flex tw-items-center tw-gap-2">
                <span className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1 tw-rounded-xl tw-text-sm tw-font-semibold ${
                  active
                    ? 'tw-bg-green-100 tw-text-green-700'
                    : 'tw-bg-red-100 tw-text-red-600'
                }`}>
                  {active ? <FaCheckCircle size={12} /> : <FaTimesCircle size={12} />}
                  {active ? 'Active' : 'Inactive'}
                </span>
                {locked && (
                  <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1 tw-rounded-xl tw-text-sm tw-font-semibold tw-bg-amber-100 tw-text-amber-700">
                    <FaLock size={11} /> Locked
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tw-px-6 tw-space-y-4">
        {/* ── Alert banners ── */}
        {!active && (
          <div className="tw-flex tw-items-start tw-gap-3 tw-px-4 tw-py-3 tw-rounded tw-bg-amber-50 tw-border tw-border-amber-200">
            <MdWarning size={18} className="tw-text-amber-500 tw-shrink-0 tw-mt-0.5" />
            <div>
              <p className="tw-text-sm tw-font-semibold tw-text-amber-800 tw-mb-1">Account Deactivated</p>
              <p className="tw-text-xs tw-text-amber-700">
                Portal, LDAP, Google Workspace and Opla CRM access have been revoked.
              </p>
            </div>
          </div>
        )}

        {locked && (
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3 tw-rounded tw-bg-red-50 tw-border tw-border-red-200">
            <div className="tw-flex tw-items-center tw-gap-2">
              <FaLock size={14} className="tw-text-red-500 tw-shrink-0" />
              <p className="tw-text-sm tw-text-red-700 tw-mb-0">
                Account locked after failed login attempts.
              </p>
            </div>
            <button
              type="button"
              onClick={handleUnlock}
              className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white tw-bg-red-600 hover:tw-bg-red-700 tw-rounded-md tw-transition-colors tw-shrink-0"
            >
              <FaUnlock size={11} /> Unlock
            </button>
          </div>
        )}

        {/* ── Tabbed content (Platform Access consolidated as a tab) ── */}
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
          <Tabs
            variant="underline"
            lazyMount
            navClassName="tw-px-4 tw-pt-2"
            contentClassName="tw-p-6"
            tabs={[
              { label: 'Detail',          icon: <FaInfoCircle size={13} />, content: <UserDetailInfo user={user} /> },
              { label: 'Platform Access', icon: <FaKey size={13} />,        content: <UserPlatformStatus user={user} /> },
              { label: 'Employee',        icon: <FaUsers size={13} />,      content: <UserDetailEmployeeMaping employee={user?.employee} /> },
              { label: 'Authorization',   icon: <FaUserShield size={13} />, content: <UserDetailAuthorization user={user} /> },
              { label: 'Activities',      icon: <FaHistory size={13} />,    content: <UserActivity /> },
            ]}
          />
        </div>

        <div className="tw-pb-8" />
      </div>
    </div>
  );
};

export default UserDetailPage;
